import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosPrivate } from "@/api/axios";
import { CountryDropdown } from "react-country-region-selector";
import { toast } from "sonner";
import { DirectoryType } from "@/lib/interfaces";

const formSchema = z.object({
  country: z.string().min(1, "Country is required"),
  name: z.string().optional(),
  person: z.string().min(1, "Person is required"),
  position: z.string().optional(),
  phone1: z.string().optional(),
  phone2: z.string().optional(),
  whatsapp1: z.string().optional(),
  whatsapp2: z.string().optional(),
  // Email 1 is compulsory: must be a valid non-empty email
  email1: z
    .string({ required_error: "Email 1 is required" })
    .min(1, "Email 1 is required")
    .email("Invalid email"),
  email2: z.string().email("Invalid email").or(z.literal("")).optional(),
  website: z.string().optional(),
  address_line1: z.string().optional(),
  address_line2: z.string().optional(),
  city: z.string().optional(),
  province_state: z.string().optional(),
  price_list: z.string().optional(),
});

interface DirectoryEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: DirectoryType | null;
}

export default function DirectoryEditDialog({ open, onOpenChange, entry }: DirectoryEditDialogProps) {
  const queryClient = useQueryClient();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      country: "",
      name: "",
      person: "",
      position: "",
      phone1: "",
      phone2: "",
      whatsapp1: "",
      whatsapp2: "",
      email1: "",
      email2: "",
      website: "",
      address_line1: "",
      address_line2: "",
      city: "",
      province_state: "",
      price_list: "",
    },
  });

  React.useEffect(() => {
    if (entry) {
      form.reset({
        country: entry.country || "",
        name: entry.name || (entry as any).company_name || (entry as any).office_name || "",
        person: entry.person || "",
        position: entry.position || "",
        phone1: entry.phone1 || "",
        phone2: entry.phone2 || "",
        whatsapp1: entry.whatsapp1 || "",
        whatsapp2: entry.whatsapp2 || "",
        email1: entry.email1 || "",
        email2: entry.email2 || "",
        website: entry.website || "",
        address_line1: entry.address_line1 || "",
        address_line2: entry.address_line2 || "",
        city: entry.city || "",
        province_state: entry.province_state || "",
        price_list: entry.price_list || (entry as any).price_list_url || "",
      });
    }
  }, [entry, form]);

  const updateEntryMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      // Clean payload: trim and drop empty strings
      const cleaned: Record<string, any> = {};
      Object.entries(data).forEach(([key, val]) => {
        if (val === undefined || val === null) return;
        if (typeof val === "string") {
          const t = val.trim();
          if (t === "") return;
          cleaned[key] = t;
        } else {
          cleaned[key] = val;
        }
      });
      const res = await axiosPrivate.put(`/dxn-directory/${entry?.id}`, cleaned);
      if (!res.data?.success) throw new Error("Failed to update entry");
      return res.data;
    },
    onSuccess: () => {
      toast.success("Directory entry updated");
      queryClient.invalidateQueries({ queryKey: ["directories"] });
      onOpenChange(false);
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || "Failed to update entry";
      toast.error(typeof msg === "string" ? msg : "Failed to update entry");
    },
  });

  function handleSubmit(data: z.infer<typeof formSchema>) {
    updateEntryMutation.mutate(data);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
  <DialogContent className="w-full max-w-full !max-w-[98vw] h-[95vh] flex flex-col p-8">
        <DialogHeader>
          <DialogTitle>Edit Directory Entry</DialogTitle>
          <DialogDescription>Update fields below and click Save Changes.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-6 flex-1 overflow-y-auto" onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="grid grid-cols-2 gap-4">
              <FormField name="country" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <FormControl>
                    <CountryDropdown
                      value={field.value}
                      onChange={field.onChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="name" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Company / Office name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="person" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Person</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="position" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Position</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="phone1" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone 1</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="phone2" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone 2</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="whatsapp1" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>WhatsApp 1</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="whatsapp2" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>WhatsApp 2</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="email1" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email 1 <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="email2" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Email 2</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="website" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Website</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              {/* Spacer to keep Address Line 1 & 2 on the same next row */}
              <div className="col-span-1" aria-hidden="true" />
              <FormField name="address_line1" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 1</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="address_line2" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 2</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="city" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="province_state" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Province/State</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="price_list" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Price List (Link)</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={updateEntryMutation.isPending}>
                {updateEntryMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
